import { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { GetEnforcementIntentsWhereFilter } from "common/graphql";
import { EnforcementIntentSummary } from "common/graphql/fragments/enforcementIntentSummary";
import { PayeeCurbArea } from "common/graphql/queries/GetPayeeCurbAreas";
import { buildStreetAddressWhereClause } from "../ZoneAreaFilterValue";

export type NoticesWhereFilter = GetEnforcementIntentsWhereFilter & { _and?: unknown[]; _or?: unknown[] };

export const isZoneFilterItem = (item: GridFilterItem): boolean =>
  item.columnField === "zone" && item.operatorValue === "equals" && Boolean(item.value);

export const isAreaFilterItem = (item: GridFilterItem): boolean =>
  item.columnField === "area" && item.operatorValue === "isAnyOf" && Boolean(item.value);

export const toZoneNamesById = (zones: { id: string; name: string }[]): Map<string, string> =>
  new Map(zones.map((zone) => [zone.id, zone.name]));

// Maps each zone name to the names of the curb areas containing a zone by that name.
export const buildAreaNamesByZoneName = (curbAreas: PayeeCurbArea[]): Map<string, string[]> => {
  const byZoneName = new Map<string, string[]>();
  curbAreas.forEach((area) => {
    area.curbAreaZones.forEach(({ zone }) => {
      const areaNames = byZoneName.get(zone.name) ?? [];
      if (!areaNames.includes(area.name)) {
        byZoneName.set(zone.name, [...areaNames, area.name]);
      }
    });
  });
  return byZoneName;
};

// Areas are matched by zone name against the session's street_address (AUT-6037).
export const getAreaNames = (row: EnforcementIntentSummary, areaNamesByZoneName: Map<string, string[]>): string => {
  const streetAddress = row.sessions[0]?.streetAddress;
  return (streetAddress && areaNamesByZoneName.get(streetAddress)?.join(", ")) || "-";
};

/**
 * Builds the notices query's where clause from a tab's base filter and its applied column filters.
 * Zone and Area filters are matched on zone names, which the caller looks up first (AUT-6037).
 */
export const buildNoticesFilter = (
  baseFilter: GetEnforcementIntentsWhereFilter,
  appliedFilters: GridFilterModel | undefined,
  zoneNamesById: Map<string, string>,
  curbAreas: PayeeCurbArea[],
): NoticesWhereFilter => {
  if (!appliedFilters) {
    return baseFilter;
  }

  const columnFilters = appliedFilters.items
    .map((item) => {
      // Both columns declare their operators through orderedStringOperators, so `equals` and
      // `contains` are the only ones that reach here. The `_ilike` branch is plate-only now;
      // notice number is pinned to `equals`, which is the difference between a 4ms index scan
      // and a scan of the client's whole partition (AUT-5789).
      if (item.columnField === "noticeNumber") {
        return {
          notice_number: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
        };
      }
      if (item.columnField === "licensePlate") {
        return {
          license_plate_number: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
        };
      }
      if (item.columnField === "status" && item.operatorValue === "equals") {
        switch (item.value.toLowerCase()) {
          case "pending":
            return {
              status: { _eq: "requires_officer_review" },
              reviews: { type: { _eq: "officer" }, reviewed_at: { _is_null: true } },
            };
          case "issued":
            return {
              ro_lookup_submitted_at: { _is_null: false },
              _not: {
                reviews: {
                  status: { _neq: "approved" },
                },
              },
            };
          case "voided":
            // The _or is load-bearing: `_neq` alone also drops rows where
            // voided_reason IS NULL, because SQL `NULL <> 'internal_review'`
            // is NULL rather than true. Without it, a notice voided with no
            // reason at all disappears from the list instead of surfacing for
            // triage. See AUT-5544.
            return {
              voided_at: { _is_null: false },
              _or: [{ voided_reason: { _is_null: true } }, { voided_reason: { _neq: "internal_review" } }],
            };
          default:
            return {};
        }
      }
      if (item.columnField === "towEligible" && item.operatorValue === "equals") {
        // "Has an open curb event." Two arms because the backing event is
        // either a park or a double park, and Hasura offers no union across
        // the two object relationships — a single arm would silently drop the
        // ~12% of MBPA intents that are double-park-backed (884 of 7,617
        // intent-sessions on 2026-09-08 — a moving figure, dated on purpose).
        //
        // Coarser than the three states the column renders: the red/amber
        // split is start_time against a two-hour threshold, and pushing that
        // down would put a now()-relative literal into the query variables,
        // changing them on every 10s poll and defeating the Apollo cache.
        //
        // The voided arm mirrors resolveTowEligibility, which suppresses voided
        // notices so the cell renders blank and exports "No". Without the same
        // suppression here, such a row would come back under "Yes" rendering
        // nothing, and be absent from "No" despite exporting "No" — the filter
        // and the displayed value disagreeing in both directions at once.
        //
        // It is currently unreachable, and kept anyway. Now that the column is
        // Pending-tab only, getDefaultFilterByVariant pins that tab's base
        // filter to status = requires_officer_review, so a voided row cannot
        // reach this predicate. Retained because it costs nothing and makes the
        // filter agree with the cell on its own terms rather than by relying on
        // another function's WHERE clause — AUT-5763 notes that widening this
        // column beyond Pending is cheap, and whoever does that should not have
        // to rediscover this mismatch. `status` is NOT NULL, so `_neq` is safe
        // here, unlike voided_reason above, which is nullable — see AUT-5544.
        const isTowEligible = {
          _and: [
            { status: { _neq: "voided" } },
            {
              _or: [
                { sessions: { park: { end_time: { _is_null: true } } } },
                { sessions: { double_park: { end_time: { _is_null: true } } } },
              ],
            },
          ],
        };
        return item.value === "Yes" ? isTowEligible : { _not: isTowEligible };
      }
      if (item.columnField === "violationType" && item.operatorValue === "equals") {
        return { violation_type: { _eq: item.value } };
      }
      if (isZoneFilterItem(item)) {
        const zoneName = zoneNamesById.get(item.value);
        return buildStreetAddressWhereClause(zoneName ? [zoneName] : []);
      }
      if (isAreaFilterItem(item)) {
        const areaIds = item.value.split(",");
        const zoneNames = curbAreas
          .filter((area) => areaIds.includes(area.id))
          .flatMap((area) => area.curbAreaZones.map(({ zone }) => zone.name));
        return buildStreetAddressWhereClause(Array.from(new Set(zoneNames)));
      }
      if (item.columnField === "issuedAt") {
        const filterDate = new Date(`${item.value}T00:00:00`);
        const dateStart = startOfDay(filterDate);
        const dateEnd = endOfDay(filterDate);

        switch (item.operatorValue) {
          case "is":
            return { issued_at: { _gte: dateStart, _lte: dateEnd } };
          case "before":
            return { issued_at: { _lt: dateStart } };
          case "after":
            return { issued_at: { _gt: dateEnd } };
        }
      }

      return undefined;
    })
    .filter(Boolean);

  if (appliedFilters.linkOperator === "and") {
    return { ...baseFilter, _and: columnFilters };
  } else {
    return { ...baseFilter, _or: columnFilters };
  }
};
